import { useMemo } from 'react'

import { cookie } from '@hozana/storage/cookies'

import { QUERY_ACTION } from 'general/constants'
import { COOKIES, COOKIES_CONFIG } from 'general/managers/cookies/constants'
import { POPINS } from 'general/managers/popins/constants'

import { getNotificationsAvailable } from 'modules/notification/functions'
import { useUser } from 'modules/user/hooks/useUser'

import { PromptType } from './constants'
import { usePromptsManagement } from './usePromptsManagement'

const HOURS_REQUESTED_SINCE_SUBSCRIPTION = 24
const DAYS_BETWEEN_REQUESTS = 10
const MAX_REQUESTS = 3

type TPushRequestHistory = { lastRequestDateMs: number; number: number }

const popinOptions = {
  reportRequest: () => {
    const { number: nbAppearances } = cookie.load<TPushRequestHistory>(COOKIES.pushRequestHistory) || {
      lastRequestDateMs: 0,
      number: 1,
    }
    cookie.save(
      COOKIES.pushRequestHistory,
      { lastRequestDateMs: Date.now(), number: nbAppearances + 1 },
      COOKIES_CONFIG.ONE_YEAR,
    )
  },
}

/**
 * ### PushRequestManager ###
 *
 * Manage automatic push request popin.
 */
export const PushRequestManager: TFC = () => {
  const me = useUser()
  const { lastRequestDateMs, number: nbAppearances } = cookie.load<TPushRequestHistory>(COOKIES.pushRequestHistory) || {
    lastRequestDateMs: 0,
    number: 1,
  }

  const show = useMemo(() => {
    // Check if is notifications are available
    if (me.isLogged && getNotificationsAvailable()) {
      // Check if is already granted
      if (Notification.permission === 'default') {
        const daysSinceLastRequest = Math.floor((Date.now() - lastRequestDateMs) / (1000 * 3600 * 24))
        const subscribedAtMs = Date.parse(me.subscribedAt)
        const hoursSinceSubscription = Math.floor((Date.now() - subscribedAtMs) / (1000 * 3600))
        // Check parameters
        if (
          hoursSinceSubscription > HOURS_REQUESTED_SINCE_SUBSCRIPTION &&
          daysSinceLastRequest > DAYS_BETWEEN_REQUESTS &&
          nbAppearances <= MAX_REQUESTS
        ) {
          return true
        }
      }
    }
    return false
  }, [me.isLogged, me.subscribedAt, nbAppearances, lastRequestDateMs])

  usePromptsManagement({
    show,
    type: PromptType.PUSH_REQUEST,
    delay: 3000, // 3s
    actionKey: QUERY_ACTION.REQUEST_PUSH,
    popinName: POPINS.PushRequestPopin,
    popinOptions,
  })

  return null
}
